import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { Bug } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { isProductionMode, isViteDevServer } from "@/lib/environment";

// Context for global layout debug state
interface LayoutDebugContextType {
  layoutDebug: boolean;
  setLayoutDebug: (value: boolean) => void;
}

const LayoutDebugContext = createContext<LayoutDebugContextType>({
  layoutDebug: false,
  setLayoutDebug: () => {},
});

const useLayoutDebug = () => useContext(LayoutDebugContext);

/**
 * Toggle button for the layout debug helper.
 * Only visible in development mode (not production).
 */
export function LayoutDebugToggle() {
  const { t } = useTranslation();
  const { layoutDebug, setLayoutDebug } = useLayoutDebug();
  const [showDebugToggle, setShowDebugToggle] = useState(false);

  useEffect(() => {
    if (!isViteDevServer()) {
      setShowDebugToggle(false);
      return;
    }

    let isMounted = true;

    isProductionMode()
      .then((value) => {
        if (isMounted) {
          setShowDebugToggle(value === false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setShowDebugToggle(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // Don't render in production mode
  if (!showDebugToggle) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={layoutDebug ? "default" : "ghost"}
            size="icon"
            className="h-9 w-9"
            onClick={() => setLayoutDebug(!layoutDebug)}
            aria-label={t("layoutDebug.title")}
          >
            <Bug className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs font-medium">{t("layoutDebug.title")}</p>
          <p className="text-xs text-muted-foreground">
            {layoutDebug ? t("layoutDebug.active") : t("layoutDebug.hint")}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Checks for table cells where child content (icons, buttons) exceeds available width.
 * Returns count of problematic cells.
 */
function useCellOverflowCheck(layoutDebug: boolean): number {
  const [overflowCount, setOverflowCount] = useState(0);

  const checkCellOverflow = useCallback(() => {
    if (!layoutDebug) {
      // Clean up any existing markers
      document.querySelectorAll("[data-cell-overflow]").forEach((el) => {
        el.removeAttribute("data-cell-overflow");
      });
      setOverflowCount(0);
      return;
    }

    let count = 0;
    const cells = document.querySelectorAll("td, th");

    // TES-1664: pre-existing debug scan complexity; this PR only labels the debug toggle.
    // fallow-ignore-next-line complexity
    cells.forEach((cell) => {
      const htmlCell = cell as HTMLElement;

      // Skip hidden cells (e.g. responsive columns)
      const isVisible = htmlCell.getClientRects().length > 0 && htmlCell.clientWidth > 0;
      if (!isVisible) {
        htmlCell.removeAttribute("data-cell-overflow");
        return;
      }

      // Only flag if the cell has actual visible scroll overflow
      // AND the content is visually clipped (not just internally wider)
      const hasVisualClipping = htmlCell.scrollWidth > htmlCell.clientWidth + 5;

      // Also check: Is there a child element protruding beyond the cell boundary?
      const cellRect = htmlCell.getBoundingClientRect();
      let hasChildOverflow = false;

      Array.from(htmlCell.children).forEach((child) => {
        const childRect = (child as HTMLElement).getBoundingClientRect();
        // Child protrudes to the right of the cell
        if (childRect.right > cellRect.right + 5) {
          hasChildOverflow = true;
        }
      });

      if (hasVisualClipping || hasChildOverflow) {
        htmlCell.setAttribute("data-cell-overflow", "true");
        count++;
      } else {
        htmlCell.removeAttribute("data-cell-overflow");
      }
    });

    setOverflowCount(count);
  }, [layoutDebug]);

  useEffect(() => {
    // Always run once (clears markers when debug is off)
    checkCellOverflow();

    // When debug is off, do not attach listeners or run periodic checks
    if (!layoutDebug) {
      return;
    }

    // Re-check on resize
    const handleResize = () => {
      requestAnimationFrame(checkCellOverflow);
    };

    window.addEventListener("resize", handleResize);

    // Also re-check after a short delay to catch dynamic content
    const timeout = setTimeout(checkCellOverflow, 500);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeout);
    };
  }, [layoutDebug, checkCellOverflow]);

  return overflowCount;
}

/**
 * Legend overlay that explains the color coding.
 * Only visible when debug mode is active.
 */
export function LayoutDebugLegend() {
  const { t } = useTranslation();
  const { layoutDebug } = useLayoutDebug();
  const cellOverflowCount = useCellOverflowCheck(layoutDebug);

  if (!layoutDebug) {
    return null;
  }

  return (
    <>
      {/* CSS injection for debug styling */}
      <style>{`
        .layout-debug * {
          outline: 1px solid hsl(var(--primary) / 0.3) !important;
        }
        .layout-debug [class*="overflow"] {
          outline: 2px dashed hsl(var(--warning)) !important;
        }
        .layout-debug [class*="min-w-"] {
          outline: 2px dashed hsl(var(--destructive)) !important;
        }
        .layout-debug [class*="truncate"] {
          background: hsl(var(--accent) / 0.2) !important;
        }
        .layout-debug [class*="whitespace-nowrap"] {
          outline: 2px dashed hsl(var(--chart-4)) !important;
        }
        .layout-debug [class*="break-all"], .layout-debug [class*="break-words"] {
          background: hsl(var(--success) / 0.2) !important;
        }
        /* Cell overflow detection - icons/buttons too wide for cell */
        .layout-debug [data-cell-overflow="true"] {
          outline: 3px solid hsl(var(--severity-critical)) !important;
          background: hsl(var(--severity-critical) / 0.15) !important;
          position: relative;
        }
        .layout-debug [data-cell-overflow="true"]::after {
          content: "⚠";
          position: absolute;
          top: 2px;
          right: 2px;
          font-size: 10px;
          color: hsl(var(--severity-critical));
          z-index: 10;
        }
      `}</style>

      {/* Legend overlay */}
      <div className="fixed bottom-4 right-4 z-[100] bg-card border rounded-lg shadow-lg p-3 max-w-xs text-xs space-y-2">
        <div className="flex items-center gap-2 font-semibold text-sm border-b pb-2 mb-2">
          <Bug className="h-4 w-4 text-primary" />
          {t("layoutDebug.title")}
        </div>
        {/* Cell overflow - new! */}
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-solid border-severity-critical bg-severity-critical/30 shrink-0 relative">
            <span className="absolute -top-0.5 -right-0.5 text-[8px]">⚠</span>
          </div>
          <span className="text-muted-foreground">
            {t("layoutDebug.cellOverflow")}
            {cellOverflowCount > 0 && (
              <span className="ml-1 text-severity-critical font-medium">({cellOverflowCount})</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-dashed border-warning shrink-0" />
          <span className="text-muted-foreground">{t("layoutDebug.overflow")}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-dashed border-destructive shrink-0" />
          <span className="text-muted-foreground">{t("layoutDebug.minWidth")}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded border-2 border-dashed border-[hsl(var(--chart-4))] shrink-0" />
          <span className="text-muted-foreground">{t("layoutDebug.nowrap")}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-accent/50 shrink-0" />
          <span className="text-muted-foreground">{t("layoutDebug.truncate")}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-success/50 shrink-0" />
          <span className="text-muted-foreground">{t("layoutDebug.breakAll")}</span>
        </div>
        <div className="flex items-center gap-2 pt-2 border-t">
          <div className="w-4 h-4 rounded border border-primary/30 shrink-0" />
          <span className="text-muted-foreground">{t("layoutDebug.allElements")}</span>
        </div>
      </div>
    </>
  );
}
